import { useState, useEffect, useCallback } from 'react'
import { useApi } from '@/hooks/useApi'
import { apiClient } from '@/api/client'
import { toast } from 'sonner'
import { formatMediumDate } from '@/utils/date'
import { ROLE_LABELS } from '@/components/AppShell'
import { EntitlementActionDialog } from '@/components/admin/EntitlementActionDialog'
import type { PendingAction } from '@/components/admin/entitlementTypes'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'

interface StaffUser {
  user_id: string
  email: string
  name: string
  role: string
  firm_name: string | null
  last_login: string | null
  created_at: string | null
  onboarding_complete: boolean
}

interface UsersResponse {
  users: StaffUser[]
  count: number
}

const VALUATION_ROLES = ['valuation_signer', 'valuation_owner']
const ROLE_OPTIONS = ['super_admin', 'cpa', 'analyst', 'firm', ...VALUATION_ROLES]

// The existing four keep their picker wording; the Valuation roles use the
// labels the rest of the product already shows for them (AppShell ROLE_LABELS).
function roleOptionLabel(r: string) {
  return VALUATION_ROLES.includes(r) ? ROLE_LABELS[r] : r.replace('_', ' ')
}

const VALUATION_FIRM_REQUIRED = 'A Valuation user must belong to a firm. Enter the firm name.'
const OWNER_FIRM_HINT =
  "Each client business needs its own firm. Owners in the same firm see each other's engagements."

interface GrantNotice {
  firm: string
  role: string
}

function roleBadge(role: string) {
  const map: Record<string, string> = {
    super_admin: 'bg-purple-100 text-purple-800',
    cpa: 'bg-blue-100 text-blue-800',
    analyst: 'bg-indigo-100 text-indigo-800',
    firm: 'bg-gray-100 text-gray-700',
    deactivated: 'bg-red-100 text-red-700',
  }
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${map[role] ?? 'bg-gray-100 text-gray-700'}`}
    >
      {role.replace('_', ' ')}
    </span>
  )
}

function fmtDate(iso: string | null) {
  if (!iso) return 'Never'
  return formatMediumDate(iso)
}

interface AddForm {
  name: string
  email: string
  role: string
  firm_name: string
}

export function UsersPage() {
  const { data, loading, error, execute } = useApi<UsersResponse>()

  const load = useCallback(() => {
    execute(() => apiClient.get<UsersResponse>('/v1/admin/users').then(r => r.data))
  }, [execute])

  useEffect(() => {
    load()
  }, [load])

  // Add staff dialog
  const [addOpen, setAddOpen] = useState(false)
  const [addForm, setAddForm] = useState<AddForm>({ name: '', email: '', role: 'cpa', firm_name: '' })
  const [addSubmitting, setAddSubmitting] = useState(false)

  // Edit dialog
  const [editUser, setEditUser] = useState<StaffUser | null>(null)
  const [editRole, setEditRole] = useState('')
  const [editFirm, setEditFirm] = useState('')
  const [editSubmitting, setEditSubmitting] = useState(false)

  // Deactivate confirm
  const [deactivateUser, setDeactivateUser] = useState<StaffUser | null>(null)
  const [deactivateSubmitting, setDeactivateSubmitting] = useState(false)

  // Valuation grant offer (D1): shown when the server says the user's firm holds
  // no active Valuation grant. Creating or editing a user never grants anything.
  const [grantNotice, setGrantNotice] = useState<GrantNotice | null>(null)
  const [grantPending, setGrantPending] = useState<PendingAction | null>(null)
  const [grantReason, setGrantReason] = useState('')
  const [grantSubmitting, setGrantSubmitting] = useState(false)

  function openGrant(notice: GrantNotice) {
    setGrantPending({ action: 'grant', accountId: notice.firm, product: 'valuation' })
    setGrantReason('onboarding')
  }

  async function submitGrant() {
    if (!grantPending || !grantNotice) return
    const reason = grantReason.trim()
    if (!reason) {
      toast.error('A reason is required.')
      return
    }
    setGrantSubmitting(true)
    try {
      await apiClient.post('/v1/admin/entitlements/grant', {
        account_id: grantPending.accountId,
        product: 'valuation',
        role: grantNotice.role,
        reason,
      })
      toast.success(`Valuation granted to ${grantPending.accountId}.`)
      setGrantPending(null)
      setGrantNotice(null)
    } catch {
      toast.error('Failed to grant.')
    } finally {
      setGrantSubmitting(false)
    }
  }

  async function handleAdd() {
    if (!addForm.name.trim() || !addForm.email.trim()) {
      toast.error('Name and email are required.')
      return
    }
    if (VALUATION_ROLES.includes(addForm.role) && !addForm.firm_name.trim()) {
      toast.error(VALUATION_FIRM_REQUIRED)
      return
    }
    setAddSubmitting(true)
    try {
      const res = await apiClient.post<{ firm_name?: string; valuation_grant_missing?: boolean; message?: string; invitation_sent?: boolean }>('/v1/admin/users', {
        name: addForm.name.trim(),
        email: addForm.email.trim().toLowerCase(),
        role: addForm.role,
        firm_name: addForm.firm_name.trim() || null,
      })
      // Show what the server actually did: invitation sent, or account made but email not sent.
      if (res?.data?.invitation_sent === false) {
        toast.error(res.data.message ?? 'Account created, but the invitation could not be sent.')
      } else {
        toast.success(res?.data?.message ?? 'Account created.')
      }
      if (res?.data?.valuation_grant_missing) {
        setGrantNotice({ firm: res.data.firm_name || addForm.firm_name.trim(), role: addForm.role })
      }
      setAddOpen(false)
      setAddForm({ name: '', email: '', role: 'cpa', firm_name: '' })
      load()
    } catch {
      toast.error('Failed to create user.')
    } finally {
      setAddSubmitting(false)
    }
  }

  function openEdit(user: StaffUser) {
    setEditUser(user)
    setEditRole(user.role)
    setEditFirm(user.firm_name ?? '')
  }

  async function handleEdit() {
    if (!editUser) return
    if (VALUATION_ROLES.includes(editRole) && !editFirm.trim()) {
      toast.error(VALUATION_FIRM_REQUIRED)
      return
    }
    setEditSubmitting(true)
    try {
      const res = await apiClient.patch<{ firm_name?: string; valuation_grant_missing?: boolean }>(
        `/v1/admin/users/${editUser.user_id}`,
        { role: editRole, firm_name: editFirm || null },
      )
      toast.success('User updated.')
      if (res?.data?.valuation_grant_missing) {
        setGrantNotice({ firm: res.data.firm_name || editFirm.trim(), role: editRole })
      }
      setEditUser(null)
      load()
    } catch {
      toast.error('Failed to update user.')
    } finally {
      setEditSubmitting(false)
    }
  }

  async function handleDeactivate() {
    if (!deactivateUser) return
    setDeactivateSubmitting(true)
    try {
      await apiClient.patch(`/v1/admin/users/${deactivateUser.user_id}`, { active: false })
      toast.success(`${deactivateUser.name} deactivated.`)
      setDeactivateUser(null)
      load()
    } catch {
      toast.error('Failed to deactivate user.')
    } finally {
      setDeactivateSubmitting(false)
    }
  }

  if (loading) return <div className="p-6 text-sm text-gray-500">Loading staff…</div>
  if (error) return <div className="p-6 text-sm text-red-600">Failed to load users.</div>

  const users = data?.users ?? []

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Staff</h1>
          <p className="text-sm text-gray-500 mt-1">{users.length} users</p>
        </div>
        <Button onClick={() => setAddOpen(true)}>+ Add Staff</Button>
      </div>

      {grantNotice && (
        <div
          role="status"
          className="mb-4 flex items-center justify-between gap-4 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900"
        >
          <span>
            {grantNotice.firm} does not have Valuation yet. This user cannot open Valuation until it is granted.
          </span>
          <Button size="sm" onClick={() => openGrant(grantNotice)}>
            Grant Valuation to {grantNotice.firm}
          </Button>
        </div>
      )}

      {users.length === 0 ? (
        <p className="text-sm text-gray-500">No users found.</p>
      ) : (
        <div className="border rounded-lg overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Firm</TableHead>
                <TableHead>Last Login</TableHead>
                <TableHead>Active</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {users.map(u => {
                const isDeactivated = u.role === 'deactivated'
                return (
                  <TableRow key={u.user_id} className={isDeactivated ? 'opacity-50' : ''}>
                    <TableCell className="font-medium text-sm">{u.name}</TableCell>
                    <TableCell className="text-sm text-gray-600">{u.email}</TableCell>
                    <TableCell>{roleBadge(u.role)}</TableCell>
                    <TableCell className="text-sm text-gray-600">{u.firm_name ?? '—'}</TableCell>
                    <TableCell className="text-sm text-gray-500">{fmtDate(u.last_login)}</TableCell>
                    <TableCell>
                      <span
                        className={`inline-block w-2 h-2 rounded-full ${isDeactivated ? 'bg-red-400' : 'bg-green-400'}`}
                      />
                    </TableCell>
                    <TableCell>
                      {!isDeactivated && (
                        <div className="flex items-center gap-2">
                          <Button variant="outline" size="sm" onClick={() => openEdit(u)}>
                            Edit
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setDeactivateUser(u)}
                          >
                            Deactivate
                          </Button>
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Add Staff dialog */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Staff Member</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="add-name">Name *</Label>
              <Input
                id="add-name"
                value={addForm.name}
                onChange={e => setAddForm(f => ({ ...f, name: e.target.value }))}
                placeholder="Full name"
              />
            </div>
            <div>
              <Label htmlFor="add-email">Email *</Label>
              <Input
                id="add-email"
                type="email"
                value={addForm.email}
                onChange={e => setAddForm(f => ({ ...f, email: e.target.value }))}
                placeholder="name@firm.com"
              />
            </div>
            <div>
              <Label htmlFor="add-role">Role *</Label>
              <Select
                value={addForm.role}
                onValueChange={val => setAddForm(f => ({ ...f, role: val ?? f.role }))}
              >
                <SelectTrigger id="add-role">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ROLE_OPTIONS.map(r => (
                    <SelectItem key={r} value={r}>
                      {roleOptionLabel(r)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="add-firm">Firm name</Label>
              <Input
                id="add-firm"
                value={addForm.firm_name}
                onChange={e => setAddForm(f => ({ ...f, firm_name: e.target.value }))}
                placeholder="e.g. Fiscal Saints"
              />
              {addForm.role === 'valuation_owner' && (
                <p className="mt-1 text-xs text-gray-500">{OWNER_FIRM_HINT}</p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleAdd} disabled={addSubmitting}>
              {addSubmitting ? 'Creating…' : 'Add & Invite'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit dialog */}
      <Dialog open={!!editUser} onOpenChange={open => !open && setEditUser(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit {editUser?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="edit-role">Role</Label>
              <Select value={editRole} onValueChange={val => setEditRole(val ?? editRole)}>
                <SelectTrigger id="edit-role">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ROLE_OPTIONS.map(r => (
                    <SelectItem key={r} value={r}>
                      {roleOptionLabel(r)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="edit-firm">Firm name</Label>
              <Input
                id="edit-firm"
                value={editFirm}
                onChange={e => setEditFirm(e.target.value)}
              />
              {editRole === 'valuation_owner' && (
                <p className="mt-1 text-xs text-gray-500">{OWNER_FIRM_HINT}</p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditUser(null)}>
              Cancel
            </Button>
            <Button onClick={handleEdit} disabled={editSubmitting}>
              {editSubmitting ? 'Saving…' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <EntitlementActionDialog
        pending={grantPending}
        reason={grantReason}
        onReasonChange={setGrantReason}
        role={grantNotice?.role ?? 'valuation_signer'}
        onRoleChange={role => setGrantNotice(n => (n ? { ...n, role } : n))}
        suggested={['onboarding']}
        submitting={grantSubmitting}
        onCancel={() => setGrantPending(null)}
        onConfirm={submitGrant}
      />

      {/* Deactivate confirm dialog */}
      <Dialog open={!!deactivateUser} onOpenChange={open => !open && setDeactivateUser(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Deactivate {deactivateUser?.name}?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-gray-600">
            Their account will be blocked from signing in. The record is preserved for audit.
            This cannot be undone from the UI — contact the database admin to reactivate.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeactivateUser(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeactivate}
              disabled={deactivateSubmitting}
            >
              {deactivateSubmitting ? 'Deactivating…' : 'Deactivate'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
